import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { 
  Users, Briefcase, Calendar, TrendingUp, Activity, Loader2, 
  MapPin, Building2, Zap, BarChart3, PieChart as PieChartIcon, 
  Target, ChevronRight
} from 'lucide-react';
import { formatDateSafe, formatFullDateSafe } from '../lib/utils';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
// Fix: Use namespace import to bypass missing exported member errors in react-router-dom
import * as ReactRouterDom from 'react-router-dom';

const { useNavigate } = ReactRouterDom as any;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboardRichInsights'],
    queryFn: async () => {
      const count = async (table: string) => (await supabase.from(table).select('*', { count: 'exact', head: true })).count || 0;
      
      const [peopleCount, engagementsCount, sessionsCount, orgsCount] = await Promise.all([
        count('people'),
        count('engagements'),
        count('sessions'),
        count('organizations')
      ]);

      const { data: recentSessions } = await supabase
        .from('sessions')
        .select('*, host_org:organizations(name), engagements(name, tracking_mode)')
        .order('session_datetime', { ascending: false })
        .limit(100);

      const { data: upcomingSessions } = await supabase
        .from('sessions')
        .select('*, host_org:organizations(name), engagements(name), session_staff(people(first_name, last_name))')
        .gte('session_datetime', new Date().toISOString())
        .order('session_datetime', { ascending: true })
        .limit(5);

      const { data: partnerRolesData } = await supabase
        .from('engagement_organizations')
        .select('organization_id, role_id, organizations(name), organization_roles(label)');

      const { data: programs } = await supabase.from('programs').select('name, program_categories(label)');
      const { data: people } = await supabase.from('people').select('city');
      const { data: attendance } = await supabase.from('attendance').select('person_id, session_id');

      return { 
        counts: { people: peopleCount, engagements: engagementsCount, sessions: sessionsCount, orgs: orgsCount },
        recentSessions: recentSessions || [],
        upcomingSessions: upcomingSessions || [],
        partnerRoles: partnerRolesData || [],
        programs: programs || [],
        people: people || [],
        attendance: attendance || []
      };
    }
  });

  const insights = useMemo(() => {
    if (!dashboardData) return null;

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const trendMap: Record<string, { month: string, headcount: number, roster: number }> = {};
    
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setUTCMonth(d.getUTCMonth() - i);
      const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
      trendMap[key] = { month: months[d.getUTCMonth()], headcount: 0, roster: 0 };
    }

    dashboardData.recentSessions.forEach(s => {
      const d = new Date(s.session_datetime);
      const key = `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
      if (trendMap[key]) {
        trendMap[key].headcount += s.headcount_count || s.headcount || 0;
        const sessionAttendance = dashboardData.attendance.filter(a => a.session_id === s.id).length;
        trendMap[key].roster += sessionAttendance;
      }
    });
    const trendData = Object.values(trendMap);

    const categoryMap: Record<string, number> = {};
    dashboardData.programs.forEach(p => {
      const cat = (p as any).program_categories?.label || 'General';
      categoryMap[cat] = (categoryMap[cat] || 0) + 1;
    });
    const mixData = Object.entries(categoryMap).map(([name, value]) => ({ name, value }));

    const partnerRoleMap: Record<string, any> = {};
    dashboardData.partnerRoles.forEach((pr: any) => {
      const orgName = pr.organizations?.name;
      const roleLabel = pr.organization_roles?.label || 'Linked Partner';
      if (orgName) {
        if (!partnerRoleMap[orgName]) partnerRoleMap[orgName] = { name: orgName };
        partnerRoleMap[orgName][roleLabel] = (partnerRoleMap[orgName][roleLabel] || 0) + 1;
      }
    });
    
    const partnerData = Object.values(partnerRoleMap)
      .sort((a, b) => {
        const totalA = Object.keys(a).filter(k => k !== 'name').reduce((sum, k) => sum + (a as any)[k], 0);
        const totalB = Object.keys(b).filter(k => k !== 'name').reduce((sum, k) => sum + (b as any)[k], 0);
        return totalB - totalA;
      })
      .slice(0, 5);

    const uniqueRoles: string[] = Array.from(new Set(dashboardData.partnerRoles.map((pr: any) => (pr.organization_roles?.label as string) || 'Linked Partner')));

    const cityMap: Record<string, number> = {};
    dashboardData.people.forEach(p => {
      if (p.city) cityMap[p.city] = (cityMap[p.city] || 0) + 1;
    });
    const geoData = Object.entries(cityMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const attendanceCounts: Record<string, number> = {};
    dashboardData.attendance.forEach(a => {
      if (a.person_id) attendanceCounts[a.person_id] = (attendanceCounts[a.person_id] || 0) + 1;
    });
    const attendees = Object.values(attendanceCounts);
    const retained = attendees.filter(count => count > 1).length;
    const retentionRate = attendees.length > 0 ? Math.round((retained / attendees.length) * 100) : 0;

    return { trendData, mixData, partnerData, uniqueRoles, geoData, retentionRate };
  }, [dashboardData]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <Loader2 size={48} className="text-slate-900 animate-spin mb-6" />
        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300">Synchronizing Global Insights</p>
      </div>
    );
  }

  const COLORS = ['#000000', '#FFDB00', '#3B82F6', '#10B981', '#F43F5E'];

  return (
    <div className="space-y-12 animate-in fade-in duration-700 pb-24 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tight">Insights</h1>
          <div className="flex items-center gap-4 mt-3">
             <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Real-time Telemetry</span>
             </div>
             <div className="px-2 py-0.5 bg-slate-100 rounded text-[8px] font-black text-slate-400 uppercase tracking-widest border border-slate-200">
                Data Refresh: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
             </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'people', value: dashboardData?.counts.people, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50', path: '/people' },
          { label: 'engagements', value: dashboardData?.counts.engagements, icon: Target, color: 'text-purple-600', bg: 'bg-purple-50', path: '/engagements' },
          { label: 'sessions', value: dashboardData?.counts.sessions, icon: Activity, color: 'text-emerald-600', bg: 'bg-emerald-50', path: '/sessions' },
          { label: 'partner organizations', value: dashboardData?.counts.orgs, icon: Building2, color: 'text-amber-600', bg: 'bg-amber-50', path: '/organizations' },
        ].map((stat) => (
          <div 
            key={stat.label} 
            onClick={() => navigate(stat.path)}
            className="bg-white p-8 rounded-lg border border-slate-100 shadow-sm transition-all hover:shadow-md hover:border-slate-900 cursor-pointer group"
          >
            <div className={`${stat.bg} ${stat.color} w-10 h-10 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
              <stat.icon size={20} />
            </div>
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5">{stat.label}</p>
            <p className="text-3xl font-black text-slate-900 tracking-tight flex items-center justify-between">
              {stat.value?.toLocaleString()}
              <ChevronRight size={16} className="text-slate-100 group-hover:text-slate-900 transition-colors" />
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-slate-900 rounded-lg shadow-xl p-8 space-y-8 flex flex-col h-[450px]">
           <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-xs font-black text-[#FFDB00] uppercase tracking-widest flex items-center gap-2"><Calendar size={16} /> Upcoming Activities</h3>
                <p className="text-[8px] font-bold text-slate-500 uppercase tracking-widest">Next 5 Scheduled operational dates</p>
              </div>
              <button onClick={() => navigate('/programs')} className="text-[#FFDB00] hover:underline text-[9px] font-black uppercase tracking-widest">Manage Portfolio</button>
           </div>
           
           <div className="space-y-4 overflow-y-auto pr-2 scrollbar-hide flex-1">
              {dashboardData?.upcomingSessions.map(session => (
                 <div 
                   key={session.id} 
                   onClick={() => navigate(`/sessions/${session.id}`)}
                   className="p-5 bg-white/5 border border-white/5 hover:bg-white/10 hover:border-[#FFDB00]/30 rounded-lg transition-all cursor-pointer group flex items-center justify-between"
                 >
                    <div className="space-y-2">
                       <p className="text-sm font-black text-white uppercase tracking-tight group-hover:text-[#FFDB00] transition-colors">{session.name || 'session'}</p>
                       {(session as any).engagements?.name && (
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest -mt-1 block">
                             Engagement: <span className="text-[#FFDB00] font-black">{(session as any).engagements.name}</span>
                          </p>
                       )}
                       <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                          <div className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                             <Calendar size={12} className="text-[#FFDB00]" /> {formatFullDateSafe(session.session_datetime)}
                          </div>
                          <div className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest truncate max-w-[200px]">
                             <Building2 size={12} className="text-[#FFDB00]" /> {session.host_org?.name || 'Local Site'}
                          </div>
                       </div>
                    </div>
                    <ChevronRight size={20} className="text-white/10 group-hover:text-[#FFDB00] transition-all group-hover:translate-x-1" />
                 </div>
              ))}
              {dashboardData?.upcomingSessions.length === 0 && (
                <div className="h-full flex flex-col items-center justify-center border border-dashed border-white/10 rounded-lg space-y-4">
                   <Calendar size={48} className="text-white/5" />
                   <p className="text-[10px] font-black text-white/20 uppercase tracking-widest">No activities imminent in the roster</p>
                </div>
              )}
           </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden flex flex-col h-[450px]">
           <div className="p-6 border-b border-slate-50">
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2"><PieChartIcon size={14} className="text-purple-500" /> Strategic Alignment</h3>
              <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Program Portfolio Distribution</p>
           </div>
           <div className="flex-1 p-6 flex flex-col items-center justify-center">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={insights?.mixData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {insights?.mixData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                     contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontSize: '10px', textTransform: 'uppercase', fontWeight: 900 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-full space-y-2 mt-6 overflow-y-auto max-h-[140px] px-2 scrollbar-hide">
                 {insights?.mixData.map((item, idx) => (
                    <div key={item.name} className="flex items-center justify-between text-[10px] font-black uppercase tracking-tight">
                       <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                          <span className="text-slate-500">{item.name}</span>
                       </div>
                       <span className="text-slate-900">{item.value}</span>
                    </div>
                 ))}
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;